const _jsxFileName = '';
function _optionalChain(ops) {
  let lastAccessLHS = undefined;
  let value = ops[0];
  let i = 1;
  while (i < ops.length) {
    const op = ops[i];
    const fn = ops[i + 1];
    i += 2;
    if ((op === 'optionalAccess' || op === 'optionalCall') && value == null) {
      return undefined;
    }
    if (op === 'access' || op === 'optionalAccess') {
      lastAccessLHS = value;
      value = fn(value);
    } else if (op === 'call' || op === 'optionalCall') {
      value = fn((...args) => value.call(lastAccessLHS, ...args));
      lastAccessLHS = undefined;
    }
  }
  return value;
}
import React, { useState, useEffect } from 'react';
import {
  Plus,
  Edit2,
  Trash2,
  Search,
  Filter,
  ArrowLeft,
  Save,
  X,
  Image as ImageIcon,
  Upload,
  Gem,
  Calculator,
  RefreshCw,
  Camera,
  Clock,
  Tag,
  CheckSquare,
  Square,
  Percent,
  Calendar,
} from 'lucide-react';

import AdminPromoModal from './AdminPromoModal';

const SITE_BASE_URL = (import.meta.env.VITE_API_URL || 'http://localhost:5001/api').replace(
  '/api',
  ''
);
const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

const PLACEHOLDER_IMAGE = 'https://placehold.co/80x80/f5f5f5/999999?text=No+Image';

// ============================================================
// FIXED: ENHANCED IMAGE RESOLVER - Properly handles all image fields
// ============================================================
const getProductImageUrl = (product, debug = false) => {
  // CRITICAL FIX: Try image_url FIRST (this is what your database returns and App.tsx now includes)
  if (
    product.image_url &&
    typeof product.image_url === 'string' &&
    product.image_url.trim() !== '' &&
    product.image_url.length > 5
  ) {
    const trimmed = product.image_url.trim();

    if (debug && _optionalChain([product, 'optionalAccess', (_6) => _6.id]) === 1) {
    }

    // Return base64 images as-is
    if (trimmed.startsWith('data:image')) {
      return trimmed;
    }

    // Return absolute URLs as-is
    if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
      return trimmed;
    }

    // Handle paths with leading slash
    if (trimmed.startsWith('/')) {
      const fullUrl = `${SITE_BASE_URL}${trimmed}`;
      return fullUrl;
    }

    // Handle paths without leading slash
    if (!trimmed.startsWith('http') && !trimmed.startsWith('data:')) {
      const fullUrl = `${SITE_BASE_URL}/${trimmed}`;
      return fullUrl;
    }

    return trimmed;
  }

  // Priority 2: Try image field
  if (
    product.image &&
    typeof product.image === 'string' &&
    product.image.trim() !== '' &&
    product.image.length > 5
  ) {
    const trimmed = product.image.trim();

    if (debug && _optionalChain([product, 'optionalAccess', (_9) => _9.id]) === 1) {
    }

    if (trimmed.startsWith('data:image')) return trimmed;
    if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) return trimmed;
    if (trimmed.startsWith('/')) return `${SITE_BASE_URL}${trimmed}`;
    if (!trimmed.startsWith('http') && !trimmed.startsWith('data:')) {
      return `${SITE_BASE_URL}/${trimmed}`;
    }
    return trimmed;
  }

  // Priority 3: Try other possible image fields
  const otherFields = [
    product.imageUrl,
    product.mainImage,
    product.main_image,
    product.product_image,
    product.productImage,
    product.img,
    product.photo,
    product.picture,
  ];

  for (const img of otherFields) {
    if (img && typeof img === 'string' && img.trim() !== '' && img.length > 5) {
      const trimmed = img.trim();
      if (debug && _optionalChain([product, 'optionalAccess', (_10) => _10.id]) === 1) {
      }
      if (trimmed.startsWith('data:image')) return trimmed;
      if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) return trimmed;
      if (trimmed.startsWith('/')) return `${SITE_BASE_URL}${trimmed}`;
      return `${SITE_BASE_URL}/${trimmed}`;
    }
  }

  // Priority 4: Try gallery images
  const galleryImages = product.galleryImages || product.gallery_images;
  if (galleryImages && Array.isArray(galleryImages) && galleryImages.length > 0) {
    for (const galleryImg of galleryImages) {
      if (
        galleryImg &&
        typeof galleryImg === 'string' &&
        galleryImg.trim() !== '' &&
        galleryImg.length > 10
      ) {
        const trimmed = galleryImg.trim();
        if (debug && _optionalChain([product, 'optionalAccess', (_11) => _11.id]) === 1) {
        }
        if (trimmed.startsWith('data:image')) return trimmed;
        if (trimmed.startsWith('http')) return trimmed;
        if (trimmed.startsWith('/')) return `${SITE_BASE_URL}${trimmed}`;
        return `${SITE_BASE_URL}/${trimmed}`;
      }
    }
  }

  // No image found
  if (debug && _optionalChain([product, 'optionalAccess', (_12) => _12.id]) === 1) {
  }
  return PLACEHOLDER_IMAGE;
};

// ============================================================
// Helper function to normalize image URL for form preview
// ============================================================
const normalizeImageUrl = (imageStr) => {
  if (!imageStr || typeof imageStr !== 'string') return PLACEHOLDER_IMAGE;

  const trimmed = imageStr.trim();
  if (!trimmed) return PLACEHOLDER_IMAGE;

  if (trimmed.startsWith('data:image')) return trimmed;
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) return trimmed;
  if (trimmed.startsWith('//')) return `https:${trimmed}`;
  if (trimmed.startsWith('/')) return `${SITE_BASE_URL}${trimmed}`;

  return `${SITE_BASE_URL}/${trimmed}`;
};

const AdminProducts = ({
  products,
  rates,
  onAdd,
  onEdit,
  onDelete,
  onNavigate,
  onRefresh,
  initialView = 'list',
  user,
}) => {
  const [view, setView] = useState(initialView);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterPromo, setFilterPromo] = useState('all');
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(null);
  const [isRemovingPromo, setIsRemovingPromo] = useState(null);
  const [imageErrors, setImageErrors] = useState({});
  const [loadingImages, setLoadingImages] = useState({});
  const [apiError, setApiError] = useState(null);
  const [isOfflineMode, setIsOfflineMode] = useState(false);

  // Selection and Promo state
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [showPromoModal, setShowPromoModal] = useState(false);
  const [selectMode, setSelectMode] = useState(false);

  // Gallery images state
  const [galleryImages, setGalleryImages] = useState([]);
  const [currentGalleryIndex, setCurrentGalleryIndex] = useState(0);

  // Categories management
  const [categories, setCategories] = useState([
    { id: '1', name: 'Jewelry', value: 'jewelry' },
    { id: '2', name: 'Bags', value: 'bags' },
    { id: '3', name: 'Fragrances', value: 'fragrances' },
    { id: '4', name: 'Watches', value: 'watches' },
    { id: '5', name: 'Accessories', value: 'accessories' },
  ]);
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');

  // Price override state
  const [useManualPrice, setUseManualPrice] = useState(false);
  const [manualPrice, setManualPrice] = useState(0);

  // Toast notification
  const [toast, setToast] = useState(null);
  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };

  // Pre-order state
  const [isPreOrder, setIsPreOrder] = useState(false);
  const [preOrderDate, setPreOrderDate] = useState('');

  // Condition options
  const conditionOptions = [
    { value: 'Brand New', label: 'Brand New', color: 'emerald', icon: '🏷️' },
    { value: 'Pre-loved', label: 'Pre-loved (Used)', color: 'amber', icon: '🔄' },
    { value: 'Vintage', label: 'Vintage', color: 'purple', icon: '📅' },
    { value: 'Like New', label: 'Like New', color: 'blue', icon: '✨' },
    { value: 'Good', label: 'Good Condition', color: 'green', icon: '👍' },
    { value: 'Fair', label: 'Fair Condition', color: 'orange', icon: '⚠️' },
  ];

  // Form State - using ExtendedProduct
  const [currentProduct, setCurrentProduct] = useState({
    id: undefined,
    name: '',
    category: 'jewelry',
    price: 0,
    description: '',
    image: '',
    galleryImages: [],
    isNew: false,
    isBestSeller: false,
    weight: '',
    karat: '18k',
    goldType: 'Saudi Gold',
    condition: 'Brand New',
    stock: 0,
    priceOverridden: false,
    isPreOrder: false,
    preOrderReleaseDate: '',
  });

  useEffect(() => {
    setView(initialView);
  }, [initialView]);

  // Reset image errors when products change
  useEffect(() => {
    setImageErrors({});
    setLoadingImages({});
  }, [products]);

  const resetForm = () => {
    setCurrentProduct({
      id: undefined,
      name: '',
      category: 'jewelry',
      price: 0,
      description: '',
      image: '',
      galleryImages: [],
      isNew: false,
      isBestSeller: false,
      weight: '',
      karat: '18k',
      goldType: 'Saudi Gold',
      condition: 'Brand New',
      stock: 0,
      priceOverridden: false,
      isPreOrder: false,
      preOrderReleaseDate: '',
    });
    setGalleryImages([]);
    setCurrentGalleryIndex(0);
    setUseManualPrice(false);
    setManualPrice(0);
    setIsPreOrder(false);
    setPreOrderDate('');
    setApiError(null);
  };

  // Update filteredProducts to include promo filter
  const filteredProducts = products.filter((product) => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = filterCategory === 'all' || product.category === filterCategory;

    const isPromo = product.is_promo === true;
    const matchesPromo =
      filterPromo === 'all' ||
      (filterPromo === 'promo' && isPromo) ||
      (filterPromo === 'non-promo' && !isPromo);

    return matchesSearch && matchesCategory && matchesPromo;
  });

  // Helper function to safely parse weight
  const parseWeight = (weight) => {
    if (weight === undefined || weight === null) return NaN;
    const weightStr = typeof weight === 'string' ? weight : String(weight);
    return parseFloat(weightStr.replace(/[^0-9.]/g, ''));
  };

  // Calculate price based on weight and metal rate
  const calculatePrice = () => {
    if (useManualPrice) return manualPrice;
    if (currentProduct.category !== 'jewelry') return currentProduct.price || 0;

    const weightVal = parseWeight(currentProduct.weight);
    if (!isNaN(weightVal) && weightVal > 0) {
      const metal = currentProduct.metal_type || 'gold';

      if (metal === 'silver' && currentProduct.silver_purity) {
        const matchedRate = rates.find(
          (r) => (r.metalType || 'gold') === 'silver' && r.karat === currentProduct.silver_purity
        );
        if (matchedRate) return weightVal * matchedRate.pricePerGram;
      } else if (currentProduct.goldType && currentProduct.karat) {
        const matchedRate = rates.find(
          (r) =>
            (r.metalType || 'gold') === 'gold' &&
            r.goldType === currentProduct.goldType &&
            r.karat === currentProduct.karat
        );
        if (matchedRate) return weightVal * matchedRate.pricePerGram;
      }
    }
    return 0;
  };

  // Auto-Compute Logic — watches all relevant metal fields
  useEffect(() => {
    if (view === 'form' && currentProduct.category === 'jewelry' && !useManualPrice) {
      const computedPrice = calculatePrice();
      if (computedPrice > 0 && Math.abs((currentProduct.price || 0) - computedPrice) > 0.01) {
        setCurrentProduct((prev) => ({ ...prev, price: computedPrice, priceOverridden: false }));
      }
    }
  }, [
    currentProduct.weight,
    currentProduct.goldType,
    currentProduct.karat,
    currentProduct.silver_purity,
    currentProduct.metal_type,
    currentProduct.category,
    view,
    rates,
    useManualPrice,
  ]);

  const handleEditClick = (product) => {
    const galleryImagesArray = Array.isArray(product.galleryImages)
      ? product.galleryImages
      : product.galleryImages
        ? [product.galleryImages]
        : [];

    const extendedProduct = product;

    const isPriceOverridden = extendedProduct.priceOverridden || false;
    setUseManualPrice(isPriceOverridden);
    setManualPrice(product.price || 0);

    const isPreOrderProduct = extendedProduct.isPreOrder || false;
    setIsPreOrder(isPreOrderProduct);
    setPreOrderDate(extendedProduct.preOrderReleaseDate || '');

    setCurrentProduct({
      ...product,
      galleryImages: galleryImagesArray,
      priceOverridden: isPriceOverridden,
      isPreOrder: isPreOrderProduct,
      preOrderReleaseDate: extendedProduct.preOrderReleaseDate || '',
    });
    setGalleryImages(galleryImagesArray);
    setView('form');
    setApiError(null);
  };

  const handleAddClick = () => {
    resetForm();
    setView('form');
  };

  const handleCancel = () => {
    resetForm();
    setView('list');
  };

  const handleAddCategory = () => {
    if (newCategoryName) {
      const categoryValue = newCategoryName.toLowerCase().replace(/\s+/g, '-');

      const newCategory = {
        id: Date.now().toString(),
        name: newCategoryName,
        value: categoryValue,
      };
      setCategories([...categories, newCategory]);
      setNewCategoryName('');
      setShowAddCategory(false);
      setCurrentProduct({ ...currentProduct, category: categoryValue });
    }
  };

  const handleManualPriceChange = (value) => {
    setManualPrice(value);
    if (useManualPrice) {
      setCurrentProduct((prev) => ({ ...prev, price: value, priceOverridden: true }));
    }
  };

  const applyPromoToProducts = async (promoData) => {
    setIsSaving(true);
    let errorMsg = null;
    try {
      const response = await fetch(`${API_BASE_URL}/products/promo`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('sparklingToken')}`,
        },
        body: JSON.stringify(promoData),
      });

      const data = await response.json();

      if (data.success) {
        showToast(`Promo applied to ${data.affected_rows} products`);
        if (onRefresh) {
          await onRefresh();
        }
        setSelectedProducts([]);
        setSelectMode(false);
      } else {
        errorMsg = data.error || 'Failed to apply promo';
        showToast('Error: ' + errorMsg);
      }
    } catch (error) {
      errorMsg = 'Failed to apply promo. Please try again.';
      showToast(errorMsg);
    } finally {
      setIsSaving(false);
    }
    if (errorMsg) throw new Error(errorMsg);
  };

  const handleImageError = (productId) => {
    setImageErrors((prev) => ({ ...prev, [productId]: true }));
    setLoadingImages((prev) => ({ ...prev, [productId]: false }));
  };

  const handleImageLoad = (productId) => {
    setLoadingImages((prev) => ({ ...prev, [productId]: false }));
  };

  const compressImage = (base64, maxWidth = 800, quality = 0.7) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.src = base64;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;

        if (width > maxWidth) {
          height = (maxWidth / width) * height;
          width = maxWidth;
        }

        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        _optionalChain([
          ctx,
          'optionalAccess',
          (_13) => _13.drawImage,
          'call',
          (_14) => _14(img, 0, 0, width, height),
        ]);

        const compressed = canvas.toDataURL('image/jpeg', quality);
        resolve(compressed);
      };
      img.onerror = () => {
        resolve(base64);
      };
    });
  };

  const handleMainImageChange = async (e) => {
    const file = _optionalChain([
      e,
      'access',
      (_15) => _15.target,
      'access',
      (_16) => _16.files,
      'optionalAccess',
      (_17) => _17[0],
    ]);
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        showToast('Image too large. Please use images under 5MB.');
        return;
      }

      if (!file.type.startsWith('image/')) {
        showToast('Please upload an image file.');
        return;
      }

      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64 = reader.result;
        try {
          const compressed = await compressImage(base64, 800, 0.7);
          setCurrentProduct((prev) => ({ ...prev, image: compressed }));
        } catch (error) {
          setCurrentProduct((prev) => ({ ...prev, image: base64 }));
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleGalleryUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setIsSaving(true);

    try {
      const newImages = [];

      for (let i = 0; i < files.length; i++) {
        const file = files[i];

        if (file.size > 5 * 1024 * 1024) {
          showToast(`Image ${file.name} is too large. Please use images under 5MB.`);
          continue;
        }

        if (!file.type.startsWith('image/')) {
          showToast(`File ${file.name} is not an image.`);
          continue;
        }

        const reader = new FileReader();

        const base64 = await new Promise((resolve) => {
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(file);
        });

        const compressed = await compressImage(base64, 600, 0.6);
        newImages.push(compressed);
      }

      if (newImages.length > 0) {
        setGalleryImages((prev) => [...prev, ...newImages]);
        setCurrentProduct((prev) => ({
          ...prev,
          galleryImages: [...(prev.galleryImages || []), ...newImages],
        }));
      }
    } catch (error) {
      showToast('Error processing images. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemoveGalleryImage = (index) => {
    setGalleryImages((prev) => prev.filter((_, i) => i !== index));
    setCurrentProduct((prev) => ({
      ...prev,
      galleryImages: _optionalChain([
        prev,
        'access',
        (_18) => _18.galleryImages,
        'optionalAccess',
        (_19) => _19.filter,
        'call',
        (_20) => _20((_, i) => i !== index),
      ]),
    }));
    if (currentGalleryIndex >= index) {
      setCurrentGalleryIndex((prev) => Math.max(0, prev - 1));
    }
  };

  // ============================================================
  // ENHANCED API FUNCTIONS with proper error handling
  // ============================================================
  const saveProductToDatabase = async (productData) => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000);

      // Log what we're sending (without full base64 for debugging)
      const logData = { ...productData };
      if (logData.image && logData.image.length > 100) {
        logData.image = `[BASE64_IMAGE: ${logData.image.length} bytes]`;
      }
      if (logData.galleryImages && logData.galleryImages.length) {
        logData.galleryImages = `[${logData.galleryImages.length} images]`;
      }

      const response = await fetch(`${API_BASE_URL}/products/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('sparklingToken')}`,
        },
        body: JSON.stringify(productData),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error ${response.status}: ${errorText.substring(0, 200)}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      if (error.name === 'AbortError') {
        throw new Error('Request timed out. Please try again.');
      }
      throw error;
    }
  };

  const updateProductInDatabase = async (productData) => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000);

      // Log what we're sending
      const logData = { ...productData };
      if (logData.image && logData.image.length > 100) {
        logData.image = `[BASE64_IMAGE: ${logData.image.length} bytes]`;
      }
      if (logData.galleryImages && logData.galleryImages.length) {
        logData.galleryImages = `[${logData.galleryImages.length} images]`;
      }

      const response = await fetch(`${API_BASE_URL}/products/update?id=${productData.id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('sparklingToken')}`,
        },
        body: JSON.stringify(productData),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Server error: ${response.status} - ${errorText.substring(0, 200)}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      if (error.name === 'AbortError') {
        throw new Error('Request timed out. Please try again.');
      }
      throw error;
    }
  };

  const deleteProductFromDatabase = async (productId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/products/delete?id=${productId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('sparklingToken')}`,
        },
        body: JSON.stringify({ id: productId }),
      });

      const data = await response.json();
      return data;
    } catch (error) {
      throw error;
    }
  };

  const saveLocally = (productData) => {
    const savedProduct = {
      id: productData.id || Date.now(),
      name: productData.name,
      description: productData.description,
      price: productData.price,
      image: productData.image || '',
      galleryImages: productData.galleryImages || [],
      category: productData.category,
      goldType: productData.goldType,
      karat: productData.karat,
      weight: productData.weight,
      condition: productData.condition,
      isNew: productData.isNew === 1,
      isBestSeller: productData.isBestSeller === 1,
      stock: productData.stock,
      ...(isPreOrder && { isPreOrder: true, preOrderReleaseDate: preOrderDate }),
      ...(useManualPrice && { priceOverridden: true }),
    };

    if (productData.id) {
      onEdit(savedProduct);
    } else {
      onAdd(savedProduct);
    }

    return { success: true, message: 'Product saved locally' };
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    setApiError(null);

    if (!currentProduct.name || !currentProduct.price || !currentProduct.category) {
      showToast('Please fill in all required fields (Name, Price, Category)');
      setIsSaving(false);
      return;
    }

    const galleryImagesArray = Array.isArray(galleryImages) ? galleryImages : [];
    const weightValue = currentProduct.weight ? currentProduct.weight.toString() : '';
    const karatValue =
      currentProduct.karat && currentProduct.karat.trim() !== '' ? currentProduct.karat : '18k';
    const goldTypeValue =
      currentProduct.goldType && currentProduct.goldType.trim() !== ''
        ? currentProduct.goldType
        : 'Saudi Gold';

    const productData = {
      id: currentProduct.id,
      name: currentProduct.name,
      description: currentProduct.description || '',
      price: currentProduct.price,
      image: currentProduct.image || '',
      galleryImages: galleryImagesArray,
      category: currentProduct.category,
      goldType: goldTypeValue,
      karat: karatValue,
      weight: weightValue,
      condition: currentProduct.condition || 'Brand New',
      isNew: currentProduct.isNew ? 1 : 0,
      isBestSeller: currentProduct.isBestSeller ? 1 : 0,
      stock: currentProduct.stock || 0,
      priceOverridden: useManualPrice ? 1 : 0,
      isPreOrder: isPreOrder ? 1 : 0,
      preOrderReleaseDate: preOrderDate || null,
    };

    try {
      let result;

      try {
        if (currentProduct.id) {
          result = await updateProductInDatabase(productData);
        } else {
          result = await saveProductToDatabase(productData);
        }

        if (result.success) {
          showToast(
            currentProduct.id ? 'Product updated successfully!' : 'Product saved successfully!'
          );

          const savedProduct = {
            id:
              result.id ||
              _optionalChain([
                result,
                'access',
                (_21) => _21.product,
                'optionalAccess',
                (_22) => _22.id,
              ]) ||
              currentProduct.id ||
              Date.now(),
            name: productData.name,
            description: productData.description,
            price: productData.price,
            image: productData.image,
            galleryImages: galleryImagesArray,
            category: productData.category,
            goldType: productData.goldType,
            karat: productData.karat,
            weight: productData.weight,
            condition: productData.condition,
            isNew: productData.isNew === 1,
            isBestSeller: productData.isBestSeller === 1,
            stock: productData.stock,
            ...(isPreOrder && { isPreOrder: true, preOrderReleaseDate: preOrderDate }),
            ...(useManualPrice && { priceOverridden: true }),
          };

          if (currentProduct.id) {
            onEdit(savedProduct);
          } else {
            onAdd(savedProduct);
          }

          if (onRefresh) {
            await onRefresh();
          }

          if (savedProduct.id) {
            setImageErrors((prev) => {
              const newErrors = { ...prev };
              delete newErrors[savedProduct.id];
              return newErrors;
            });
          }

          handleCancel();
        } else {
          showToast('Error: ' + (result.error || result.message || 'Unknown error'));
        }
      } catch (apiError) {
        setApiError(apiError.message || 'Connection error. Saved locally.');
        setIsOfflineMode(true);

        const localResult = saveLocally(productData);
        if (localResult.success) {
          showToast(
            'Product saved locally (offline mode). It will sync when connection is restored.'
          );
          handleCancel();
        }
      }
    } catch (error) {
      showToast('Connection error. Please check your network and try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (
      window.confirm('Are you sure you want to delete this item? This action cannot be undone.')
    ) {
      setIsDeleting(id);
      try {
        const result = await deleteProductFromDatabase(id);
        if (result.success) {
          onDelete(id);
          if (onRefresh) {
            await onRefresh();
          }
          showToast('Product deleted successfully!');
        } else {
          showToast('Error: ' + (result.error || 'Failed to delete product'));
        }
      } catch (error) {
        showToast('Connection error. Please try again.');
      } finally {
        setIsDeleting(null);
      }
    }
  };

  const handleRemovePromo = async (id) => {
    setIsRemovingPromo(id);
    try {
      const response = await fetch(`${API_BASE_URL}/products/remove-promo`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('sparklingToken')}`,
        },
        body: JSON.stringify({ product_ids: [id] }),
      });
      const data = await response.json();
      if (data.success) {
        showToast('Promo removed successfully');
        if (onRefresh) await onRefresh();
      } else {
        showToast('Error: ' + (data.error || 'Failed to remove promo'));
      }
    } catch (error) {
      showToast('Connection error. Please try again.');
    } finally {
      setIsRemovingPromo(null);
    }
  };

  const getConditionIcon = (condition) => {
    const option = conditionOptions.find((opt) => opt.value === condition);
    return _optionalChain([option, 'optionalAccess', (_23) => _23.icon]) || '🏷️';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No end date';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString();
    } catch (e) {
      return dateString;
    }
  };

  // Track if we've logged debug info

  return React.createElement(
    'div',
    {
      className: 'pt-44 pb-12 min-h-screen bg-stone-50',
      __self: this,
      __source: { fileName: _jsxFileName, lineNumber: 881 },
    },

    /* Toast Notification */
    toast &&
      React.createElement(
        'div',
        {
          className: `fixed top-6 right-6 z-[999] flex items-center gap-3 px-5 py-4 rounded-sm shadow-xl text-sm font-medium transition-all animate-fade-in ${toast.type === 'error' ? 'bg-red-600 text-white' : 'bg-stone-900 text-white'}`,
          style: { minWidth: '280px' },
        },
        React.createElement(
          'span',
          { className: toast.type === 'error' ? 'text-red-200' : 'text-gold-400' },
          toast.type === 'error' ? '✕' : '✓'
        ),
        toast.message
      ),
    React.createElement(
      'div',
      {
        className: 'max-w-7xl mx-auto px-6',
        __self: this,
        __source: { fileName: _jsxFileName, lineNumber: 882 },
      },

      /* Header */
      React.createElement(
        'div',
        {
          className: 'flex flex-col md:flex-row justify-between items-center mb-8 gap-4',
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 885 },
        },
        React.createElement(
          'div',
          { __self: this, __source: { fileName: _jsxFileName, lineNumber: 886 } },
          (user?.role === 'admin' || (user?.permissions || []).includes('view_dashboard')) &&
            React.createElement(
              'button',
              {
                onClick: () => onNavigate('admin-dashboard'),
                className:
                  'flex items-center gap-2 text-stone-500 hover:text-stone-900 text-xs uppercase tracking-widest mb-2 transition-colors',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 887 },
              },

              React.createElement(ArrowLeft, {
                size: 14,
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 891 },
              }),
              ' Back to Dashboard'
            ),
          React.createElement(
            'h1',
            {
              className: 'font-serif text-3xl text-stone-900',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 893 },
            },
            'Inventory Management'
          )
        ),
        view === 'list' &&
          React.createElement(
            'div',
            {
              className: 'flex gap-2',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 897 },
            },
            selectMode
              ? React.createElement(
                  React.Fragment,
                  null,
                  React.createElement(
                    'button',
                    {
                      onClick: () => {
                        if (selectedProducts.length === filteredProducts.length) {
                          setSelectedProducts([]);
                        } else {
                          setSelectedProducts(filteredProducts.map((p) => p.id));
                        }
                      },
                      className:
                        'px-4 py-3 border border-stone-300 text-stone-600 text-xs uppercase tracking-widest hover:bg-stone-50 transition-colors flex items-center gap-2',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 900 },
                    },

                    React.createElement(CheckSquare, {
                      size: 14,
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 910 },
                    }),
                    selectedProducts.length === filteredProducts.length
                      ? 'Deselect All'
                      : 'Select All'
                  ),
                  selectedProducts.length > 0 &&
                    React.createElement(
                      'button',
                      {
                        onClick: () => setShowPromoModal(true),
                        className:
                          'px-4 py-3 bg-gold-600 text-white text-xs uppercase tracking-widest hover:bg-gold-700 transition-colors flex items-center gap-2',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 914 },
                      },

                      React.createElement(Tag, {
                        size: 14,
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 918 },
                      }),
                      ' Add Promo (',
                      selectedProducts.length,
                      ')'
                    ),
                  React.createElement(
                    'button',
                    {
                      onClick: () => {
                        setSelectMode(false);
                        setSelectedProducts([]);
                      },
                      className:
                        'px-4 py-3 border border-stone-300 text-stone-600 text-xs uppercase tracking-widest hover:bg-stone-50 transition-colors',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 921 },
                    },
                    'Cancel'
                  )
                )
              : React.createElement(
                  React.Fragment,
                  null,
                  React.createElement(
                    'button',
                    {
                      onClick: () => setSelectMode(true),
                      className:
                        'px-4 py-3 border border-stone-300 text-stone-600 text-xs uppercase tracking-widest hover:bg-stone-50 transition-colors flex items-center gap-2',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 933 },
                    },

                    React.createElement(Tag, {
                      size: 14,
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 937 },
                    }),
                    ' Add Promo'
                  ),
                  React.createElement(
                    'button',
                    {
                      onClick: handleAddClick,
                      className:
                        'px-4 py-3 bg-stone-900 text-white text-xs uppercase tracking-widest hover:bg-gold-600 transition-colors flex items-center gap-2 shadow-lg',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 939 },
                    },

                    React.createElement(Plus, {
                      size: 14,
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 943 },
                    }),
                    ' Add Product'
                  )
                )
          )
      ),

      /* API Error Banner */
      apiError &&
        React.createElement(
          'div',
          {
            className:
              'mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm flex items-center justify-between',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 953 },
          },
          React.createElement(
            'span',
            {
              className: 'text-sm',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 954 },
            },
            apiError
          ),
          React.createElement(
            'button',
            {
              onClick: () => setApiError(null),
              className: 'text-red-500 hover:text-red-700',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 955 },
            },
            React.createElement(X, {
              size: 16,
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 956 },
            })
          )
        ),

      view === 'list'
        ? /* LIST VIEW */
          React.createElement(
            'div',
            {
              className: 'bg-white border border-stone-200 shadow-sm rounded-sm overflow-hidden',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 963 },
            },
            /* Toolbar */
            React.createElement(
              'div',
              {
                className:
                  'p-6 border-b border-stone-100 flex flex-col md:flex-row gap-4 justify-between bg-stone-50/30',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 965 },
              },
              React.createElement(
                'div',
                {
                  className: 'relative flex-1 max-w-md',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 966 },
                },
                React.createElement(Search, {
                  className: 'absolute left-3 top-1/2 -translate-y-1/2 text-stone-400',
                  size: 16,
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 967 },
                }),
                React.createElement('input', {
                  type: 'text',
                  placeholder: 'Search products...',
                  value: searchTerm,
                  onChange: (e) => setSearchTerm(e.target.value),
                  className:
                    'w-full pl-10 pr-4 py-2 border border-stone-200 rounded-sm focus:border-gold-500 focus:outline-none text-sm',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 968 },
                })
              ),
              React.createElement(
                'div',
                {
                  className: 'flex items-center gap-4',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 976 },
                },
                React.createElement(
                  'div',
                  {
                    className: 'flex items-center gap-2',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 977 },
                  },
                  React.createElement(Tag, {
                    size: 16,
                    className: 'text-stone-400',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 978 },
                  }),
                  React.createElement(
                    'select',
                    {
                      value: filterPromo,
                      onChange: (e) => setFilterPromo(e.target.value),
                      className:
                        'border border-stone-200 rounded-sm px-3 py-2 text-sm focus:border-gold-500 focus:outline-none',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 979 },
                    },

                    React.createElement(
                      'option',
                      {
                        value: 'all',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 984 },
                      },
                      'All Products'
                    ),
                    React.createElement(
                      'option',
                      {
                        value: 'promo',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 985 },
                      },
                      'Promo Only'
                    ),
                    React.createElement(
                      'option',
                      {
                        value: 'non-promo',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 986 },
                      },
                      'Non-Promo'
                    )
                  )
                ),

                React.createElement(
                  'div',
                  {
                    className: 'flex items-center gap-2',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 990 },
                  },
                  React.createElement(Filter, {
                    size: 16,
                    className: 'text-stone-400',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 991 },
                  }),
                  React.createElement(
                    'select',
                    {
                      value: filterCategory,
                      onChange: (e) => setFilterCategory(e.target.value),
                      className:
                        'border border-stone-200 rounded-sm px-3 py-2 text-sm focus:border-gold-500 focus:outline-none',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 992 },
                    },

                    React.createElement(
                      'option',
                      {
                        value: 'all',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 997 },
                      },
                      'All Categories'
                    ),
                    categories.map((cat) =>
                      React.createElement(
                        'option',
                        {
                          key: cat.id,
                          value: cat.value,
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 999 },
                        },
                        cat.name
                      )
                    )
                  )
                )
              )
            ),

            /* Table */
            React.createElement(
              'div',
              {
                className: 'overflow-x-auto',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 1007 },
              },
              React.createElement(
                'table',
                {
                  className: 'w-full text-left',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 1008 },
                },
                React.createElement(
                  'thead',
                  {
                    className:
                      'bg-stone-100 text-stone-500 text-[10px] uppercase tracking-widest font-bold',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 1009 },
                  },
                  React.createElement(
                    'tr',
                    { __self: this, __source: { fileName: _jsxFileName, lineNumber: 1010 } },
                    selectMode &&
                      React.createElement('th', {
                        className: 'px-4 py-4 w-12',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 1011 },
                      }),
                    React.createElement(
                      'th',
                      {
                        className: 'px-6 py-4',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 1012 },
                      },
                      'Image'
                    ),
                    React.createElement(
                      'th',
                      {
                        className: 'px-6 py-4',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 1013 },
                      },
                      'Product Name'
                    ),
                    React.createElement(
                      'th',
                      {
                        className: 'px-6 py-4',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 1014 },
                      },
                      'Category'
                    ),
                    React.createElement(
                      'th',
                      {
                        className: 'px-6 py-4',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 1015 },
                      },
                      'Price (₱)'
                    ),
                    React.createElement(
                      'th',
                      {
                        className: 'px-6 py-4',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 1016 },
                      },
                      'Promo'
                    ),
                    React.createElement(
                      'th',
                      {
                        className: 'px-6 py-4',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 1017 },
                      },
                      'Details'
                    ),
                    React.createElement(
                      'th',
                      {
                        className: 'px-6 py-4 text-right',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 1018 },
                      },
                      'Actions'
                    )
                  )
                ),
                React.createElement(
                  'tbody',
                  {
                    className: 'divide-y divide-stone-100',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 1021 },
                  },
                  filteredProducts.map((product, index) => {
                    const extendedProduct = product;
                    const isPromo = extendedProduct.is_promo === true;
                    const promoPrice = extendedProduct.promo_price;
                    const promoPercentage = extendedProduct.promo_percentage;
                    const promoLabel = extendedProduct.promo_label;
                    const promoEndDate = extendedProduct.promo_end_date;

                    // Debug first product only
                    const isDebug = index === 0;
                    const productImageUrl = getProductImageUrl(product, isDebug);
                    const hasImage =
                      productImageUrl !== PLACEHOLDER_IMAGE && !imageErrors[product.id];

                    return React.createElement(
                      'tr',
                      {
                        key: product.id,
                        className: `hover:bg-stone-50/50 transition-colors ${isPromo ? 'bg-red-50/30' : ''}`,
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 1036 },
                      },
                      selectMode &&
                        React.createElement(
                          'td',
                          {
                            className: 'px-4 py-4',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 1038 },
                          },
                          React.createElement(
                            'button',
                            {
                              onClick: (e) => {
                                e.stopPropagation();
                                setSelectedProducts((prev) =>
                                  prev.includes(product.id)
                                    ? prev.filter((id) => id !== product.id)
                                    : [...prev, product.id]
                                );
                              },
                              className: 'focus:outline-none',
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 1039 },
                            },

                            selectedProducts.includes(product.id)
                              ? React.createElement(CheckSquare, {
                                  size: 18,
                                  className: 'text-gold-600',
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 1051 },
                                })
                              : React.createElement(Square, {
                                  size: 18,
                                  className: 'text-stone-400',
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 1053 },
                                })
                          )
                        ),
                      React.createElement(
                        'td',
                        {
                          className: 'px-6 py-4',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1058 },
                        },
                        React.createElement(
                          'div',
                          {
                            className:
                              'relative w-12 h-12 bg-stone-100 rounded-sm overflow-hidden border border-stone-200 flex items-center justify-center',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 1059 },
                          },
                          loadingImages[product.id]
                            ? React.createElement(
                                'div',
                                {
                                  className: 'w-full h-full flex items-center justify-center',
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 1061 },
                                },
                                React.createElement(RefreshCw, {
                                  size: 16,
                                  className: 'animate-spin text-stone-300',
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 1062 },
                                })
                              )
                            : hasImage
                              ? React.createElement('img', {
                                  src: productImageUrl,
                                  alt: product.name,
                                  className: 'w-full h-full object-cover',
                                  onError: () => handleImageError(product.id),
                                  onLoad: () => handleImageLoad(product.id),
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 1065 },
                                })
                              : React.createElement(
                                  'div',
                                  {
                                    className:
                                      'w-full h-full flex flex-col items-center justify-center bg-stone-100',
                                    __self: this,
                                    __source: { fileName: _jsxFileName, lineNumber: 1073 },
                                  },
                                  React.createElement(ImageIcon, {
                                    size: 16,
                                    className: 'text-stone-400 mb-1',
                                    __self: this,
                                    __source: { fileName: _jsxFileName, lineNumber: 1074 },
                                  }),
                                  React.createElement(
                                    'span',
                                    {
                                      className: 'text-[8px] text-stone-400',
                                      __self: this,
                                      __source: { fileName: _jsxFileName, lineNumber: 1075 },
                                    },
                                    'No Image'
                                  )
                                ),
                          isPromo &&
                            React.createElement(
                              'div',
                              {
                                className:
                                  'absolute inset-0 bg-red-600/10 flex items-center justify-center',
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 1079 },
                              },
                              React.createElement(
                                'span',
                                {
                                  className:
                                    'bg-red-600 text-white text-[8px] px-1 py-0.5 rounded-sm uppercase font-bold rotate-[-20deg] shadow-lg',
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 1080 },
                                },
                                'PROMO'
                              )
                            )
                        )
                      ),
                      React.createElement(
                        'td',
                        {
                          className: 'px-6 py-4',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1087 },
                        },
                        React.createElement(
                          'span',
                          {
                            className: 'font-serif text-stone-900 block',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 1088 },
                          },
                          product.name
                        ),
                        React.createElement(
                          'span',
                          {
                            className: 'text-xs text-stone-400 truncate max-w-[200px] block',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 1089 },
                          },
                          product.description
                        )
                      ),
                      React.createElement(
                        'td',
                        {
                          className: 'px-6 py-4',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1091 },
                        },
                        React.createElement(
                          'span',
                          {
                            className:
                              'text-xs uppercase tracking-wider text-stone-600 bg-stone-100 px-2 py-1 rounded',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 1092 },
                          },
                          product.category
                        )
                      ),
                      React.createElement(
                        'td',
                        {
                          className: 'px-6 py-4',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1094 },
                        },
                        React.createElement(
                          'div',
                          {
                            className: 'flex flex-col',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 1095 },
                          },
                          isPromo && promoPrice
                            ? React.createElement(
                                React.Fragment,
                                null,
                                React.createElement(
                                  'span',
                                  {
                                    className: 'text-xs text-stone-400 line-through',
                                    __self: this,
                                    __source: { fileName: _jsxFileName, lineNumber: 1098 },
                                  },
                                  '₱ ',
                                  product.price.toLocaleString()
                                ),
                                React.createElement(
                                  'span',
                                  {
                                    className: 'font-medium text-red-600',
                                    __self: this,
                                    __source: { fileName: _jsxFileName, lineNumber: 1099 },
                                  },
                                  '₱ ',
                                  promoPrice.toLocaleString()
                                )
                              )
                            : React.createElement(
                                'span',
                                {
                                  className: 'font-medium text-stone-900',
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 1102 },
                                },
                                '₱ ',
                                product.price.toLocaleString()
                              )
                        )
                      ),
                      React.createElement(
                        'td',
                        {
                          className: 'px-6 py-4',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1106 },
                        },
                        isPromo
                          ? React.createElement(
                              'div',
                              {
                                className: 'flex flex-col gap-1',
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 1108 },
                              },
                              React.createElement(
                                'span',
                                {
                                  className:
                                    'bg-red-600 text-white text-[9px] px-2 py-0.5 rounded-full w-fit flex items-center gap-1',
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 1109 },
                                },
                                React.createElement(Tag, {
                                  size: 10,
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 1110 },
                                }),
                                ' ',
                                promoLabel || 'SALE'
                              ),
                              promoPercentage &&
                                React.createElement(
                                  'span',
                                  {
                                    className: 'text-[9px] text-red-600 flex items-center gap-1',
                                    __self: this,
                                    __source: { fileName: _jsxFileName, lineNumber: 1113 },
                                  },
                                  React.createElement(Percent, {
                                    size: 10,
                                    __self: this,
                                    __source: { fileName: _jsxFileName, lineNumber: 1114 },
                                  }),
                                  ' ',
                                  promoPercentage,
                                  '% OFF'
                                ),
                              promoEndDate &&
                                React.createElement(
                                  'span',
                                  {
                                    className: 'text-[8px] text-stone-500 flex items-center gap-1',
                                    __self: this,
                                    __source: { fileName: _jsxFileName, lineNumber: 1118 },
                                  },
                                  React.createElement(Calendar, {
                                    size: 8,
                                    __self: this,
                                    __source: { fileName: _jsxFileName, lineNumber: 1119 },
                                  }),
                                  ' Until ',
                                  formatDate(promoEndDate)
                                )
                            )
                          : React.createElement(
                              'span',
                              {
                                className: 'text-xs text-stone-400 italic',
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 1124 },
                              },
                              'No promo'
                            )
                      ),
                      React.createElement(
                        'td',
                        {
                          className: 'px-6 py-4',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1127 },
                        },
                        React.createElement(
                          'div',
                          {
                            className: 'flex flex-col gap-1',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 1128 },
                          },
                          product.condition &&
                            React.createElement(
                              'span',
                              {
                                className: `text-[9px] uppercase tracking-widest px-1 rounded w-fit flex items-center gap-1 ${
                                  product.condition === 'Brand New'
                                    ? 'text-emerald-600 bg-emerald-50'
                                    : product.condition === 'Pre-loved'
                                      ? 'text-amber-600 bg-amber-50'
                                      : product.condition === 'Vintage'
                                        ? 'text-purple-600 bg-purple-50'
                                        : product.condition === 'Like New'
                                          ? 'text-blue-600 bg-blue-50'
                                          : product.condition === 'Good'
                                            ? 'text-green-600 bg-green-50'
                                            : product.condition === 'Fair'
                                              ? 'text-orange-600 bg-orange-50'
                                              : 'text-stone-600 bg-stone-50'
                                }`,
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 1130 },
                              },
                              React.createElement(
                                'span',
                                {
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 1139 },
                                },
                                getConditionIcon(product.condition)
                              ),
                              product.condition === 'Pre-loved' ? 'Pre-loved' : product.condition
                            ),
                          product.isNew &&
                            React.createElement(
                              'span',
                              {
                                className:
                                  'text-[9px] uppercase tracking-widest text-blue-600 bg-blue-50 w-fit px-1 rounded',
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 1143 },
                              },
                              'New'
                            ),
                          product.isBestSeller &&
                            React.createElement(
                              'span',
                              {
                                className:
                                  'text-[9px] uppercase tracking-widest text-gold-600 bg-gold-50 w-fit px-1 rounded',
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 1144 },
                              },
                              'Bestseller'
                            ),
                          extendedProduct.isPreOrder &&
                            React.createElement(
                              'span',
                              {
                                className:
                                  'text-[9px] uppercase tracking-widest text-purple-600 bg-purple-50 w-fit px-1 rounded flex items-center gap-1',
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 1146 },
                              },
                              React.createElement(Clock, {
                                size: 10,
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 1147 },
                              }),
                              ' Pre-Order'
                            ),
                          /* ONLY show jewelry-specific fields for jewelry category */
                          product.category === 'jewelry' &&
                            React.createElement(
                              React.Fragment,
                              null,
                              product.weight &&
                                React.createElement(
                                  'span',
                                  {
                                    className: 'text-[9px] text-stone-500',
                                    __self: this,
                                    __source: { fileName: _jsxFileName, lineNumber: 1153 },
                                  },
                                  product.weight
                                ),
                              product.karat &&
                                React.createElement(
                                  'span',
                                  {
                                    className: 'text-[9px] text-stone-500',
                                    __self: this,
                                    __source: { fileName: _jsxFileName, lineNumber: 1154 },
                                  },
                                  product.karat
                                ),
                              product.goldType &&
                                React.createElement(
                                  'span',
                                  {
                                    className: 'text-[9px] text-stone-500',
                                    __self: this,
                                    __source: { fileName: _jsxFileName, lineNumber: 1155 },
                                  },
                                  product.goldType
                                )
                            ),
                          product.galleryImages &&
                            Array.isArray(product.galleryImages) &&
                            product.galleryImages.length > 0 &&
                            React.createElement(
                              'span',
                              {
                                className: 'text-[9px] text-stone-500 flex items-center gap-1',
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 1159 },
                              },
                              React.createElement(Camera, {
                                size: 10,
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 1160 },
                              }),
                              ' ',
                              product.galleryImages.length,
                              ' photos'
                            ),
                          extendedProduct.priceOverridden &&
                            React.createElement(
                              'span',
                              {
                                className:
                                  'text-[9px] text-amber-600 bg-amber-50 w-fit px-1 rounded',
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 1164 },
                              },
                              'Manual Price'
                            )
                        )
                      ),
                      React.createElement(
                        'td',
                        {
                          className: 'px-6 py-4 text-right',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1168 },
                        },
                        React.createElement(
                          'div',
                          {
                            className: 'flex items-center justify-end gap-2',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 1169 },
                          },
                          React.createElement(
                            'button',
                            {
                              onClick: () => handleEditClick(product),
                              className:
                                'p-2 text-stone-500 hover:text-stone-900 hover:bg-stone-100 rounded transition-colors',
                              title: 'Edit',
                              disabled: isDeleting === product.id,
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 1170 },
                            },

                            React.createElement(Edit2, {
                              size: 16,
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 1176 },
                            })
                          ),
                          isPromo &&
                            React.createElement(
                              'button',
                              {
                                onClick: () => handleRemovePromo(product.id),
                                className:
                                  'p-2 text-stone-400 hover:text-orange-600 hover:bg-orange-50 rounded transition-colors disabled:opacity-50',
                                title: 'Remove Promo',
                                disabled:
                                  isRemovingPromo === product.id || isDeleting === product.id,
                              },
                              isRemovingPromo === product.id
                                ? React.createElement(RefreshCw, {
                                    size: 16,
                                    className: 'animate-spin',
                                  })
                                : React.createElement(Tag, { size: 16 })
                            ),
                          React.createElement(
                            'button',
                            {
                              onClick: () => handleDelete(product.id),
                              className:
                                'p-2 text-stone-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50',
                              title: 'Delete',
                              disabled: isDeleting === product.id,
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 1178 },
                            },

                            isDeleting === product.id
                              ? React.createElement(RefreshCw, {
                                  size: 16,
                                  className: 'animate-spin',
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 1185 },
                                })
                              : React.createElement(Trash2, {
                                  size: 16,
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 1187 },
                                })
                          )
                        )
                      )
                    );
                  })
                )
              )
            )
          )
        : /* FORM VIEW - unchanged */
          React.createElement(
            'div',
            {
              className:
                'bg-white border border-stone-200 shadow-xl rounded-sm max-w-4xl mx-auto overflow-hidden animate-fade-in',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 1201 },
            },
            React.createElement(
              'div',
              {
                className:
                  'px-8 py-6 border-b border-stone-100 bg-stone-50 flex justify-between items-center',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 1202 },
              },
              React.createElement(
                'h2',
                {
                  className: 'font-serif text-2xl text-stone-900',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 1203 },
                },
                currentProduct.id ? 'Edit Product' : 'Add New Product'
              ),
              React.createElement(
                'button',
                {
                  onClick: handleCancel,
                  className: 'text-stone-400 hover:text-stone-900',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 1206 },
                },
                React.createElement(X, {
                  size: 24,
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 1207 },
                })
              )
            ),

            React.createElement(
              'form',
              {
                onSubmit: handleSave,
                className: 'p-8 grid grid-cols-1 md:grid-cols-2 gap-8',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 1211 },
              },
              /* Left Column */
              React.createElement(
                'div',
                {
                  className: 'space-y-6',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 1213 },
                },
                React.createElement(
                  'div',
                  { __self: this, __source: { fileName: _jsxFileName, lineNumber: 1214 } },
                  React.createElement(
                    'label',
                    {
                      className:
                        'block text-xs uppercase tracking-widest text-stone-500 font-bold mb-2',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 1215 },
                    },
                    'Product Name *'
                  ),
                  React.createElement('input', {
                    type: 'text',
                    value: currentProduct.name || '',
                    onChange: (e) => setCurrentProduct({ ...currentProduct, name: e.target.value }),
                    className:
                      'w-full border border-stone-300 p-3 text-sm rounded-sm focus:border-gold-500 focus:outline-none',
                    required: true,
                    placeholder: 'e.g., Diamond Necklace',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 1218 },
                  })
                ),

                React.createElement(
                  'div',
                  { __self: this, __source: { fileName: _jsxFileName, lineNumber: 1228 } },
                  React.createElement(
                    'label',
                    {
                      className:
                        'block text-xs uppercase tracking-widest text-stone-500 font-bold mb-2',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 1229 },
                    },
                    'Description'
                  ),
                  React.createElement('textarea', {
                    value: currentProduct.description || '',
                    onChange: (e) =>
                      setCurrentProduct({ ...currentProduct, description: e.target.value }),
                    rows: 4,
                    className:
                      'w-full border border-stone-300 p-3 text-sm rounded-sm focus:border-gold-500 focus:outline-none resize-none',
                    placeholder: 'Product description...',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 1232 },
                  })
                ),

                React.createElement(
                  'div',
                  { __self: this, __source: { fileName: _jsxFileName, lineNumber: 1241 } },
                  React.createElement(
                    'label',
                    {
                      className:
                        'block text-xs uppercase tracking-widest text-stone-500 font-bold mb-2',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 1242 },
                    },
                    'Category *'
                  ),
                  React.createElement(
                    'div',
                    {
                      className: 'flex gap-2',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 1245 },
                    },
                    React.createElement(
                      'select',
                      {
                        value: currentProduct.category || 'jewelry',
                        onChange: (e) =>
                          setCurrentProduct({ ...currentProduct, category: e.target.value }),
                        className:
                          'flex-1 border border-stone-300 p-3 text-sm rounded-sm focus:border-gold-500 focus:outline-none',
                        required: true,
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 1246 },
                      },

                      categories.map((cat) =>
                        React.createElement(
                          'option',
                          {
                            key: cat.id,
                            value: cat.value,
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 1253 },
                          },
                          cat.name
                        )
                      )
                    ),
                    React.createElement(
                      'button',
                      {
                        type: 'button',
                        onClick: () => setShowAddCategory(true),
                        className: 'px-3 border border-stone-300 rounded-sm hover:bg-stone-50',
                        title: 'Add new category',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 1256 },
                      },

                      React.createElement(Plus, {
                        size: 18,
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 1262 },
                      })
                    )
                  ),

                  showAddCategory &&
                    React.createElement(
                      'div',
                      {
                        className: 'mt-2 p-3 bg-stone-50 border border-stone-200 rounded-sm',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 1267 },
                      },
                      React.createElement('input', {
                        type: 'text',
                        value: newCategoryName,
                        onChange: (e) => setNewCategoryName(e.target.value),
                        placeholder: 'Category name',
                        className:
                          'w-full p-2 text-sm border border-stone-300 rounded-sm focus:border-gold-500 focus:outline-none mb-2',
                        autoFocus: true,
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 1268 },
                      }),
                      React.createElement(
                        'div',
                        {
                          className: 'flex gap-2',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1276 },
                        },
                        React.createElement(
                          'button',
                          {
                            type: 'button',
                            onClick: handleAddCategory,
                            className:
                              'px-3 py-1 bg-stone-900 text-white text-xs uppercase tracking-widest hover:bg-gold-600 transition-colors rounded-sm',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 1277 },
                          },
                          'Add'
                        ),
                        React.createElement(
                          'button',
                          {
                            type: 'button',
                            onClick: () => setShowAddCategory(false),
                            className:
                              'px-3 py-1 border border-stone-300 text-stone-600 text-xs uppercase tracking-widest hover:bg-stone-50 transition-colors rounded-sm',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 1284 },
                          },
                          'Cancel'
                        )
                      )
                    )
                ),

                React.createElement(
                  'div',
                  {
                    className: 'grid grid-cols-2 gap-4',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 1296 },
                  },
                  React.createElement(
                    'div',
                    { __self: this, __source: { fileName: _jsxFileName, lineNumber: 1297 } },
                    React.createElement(
                      'label',
                      {
                        className:
                          'block text-xs uppercase tracking-widest text-stone-500 font-bold mb-2',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 1298 },
                      },
                      'Price (₱) *'
                    ),
                    React.createElement(
                      'div',
                      {
                        className: 'relative',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 1301 },
                      },
                      React.createElement(
                        'span',
                        {
                          className: 'absolute left-3 top-1/2 -translate-y-1/2 text-stone-400',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1302 },
                        },
                        '₱'
                      ),
                      React.createElement('input', {
                        type: 'number',
                        value: currentProduct.price || '',
                        onChange: (e) => {
                          const value = parseFloat(e.target.value);
                          setCurrentProduct({ ...currentProduct, price: isNaN(value) ? 0 : value });
                          if (!useManualPrice && currentProduct.category === 'jewelry') {
                            setUseManualPrice(true);
                          }
                        },
                        className:
                          'w-full pl-8 pr-4 py-3 border border-stone-300 text-sm rounded-sm focus:border-gold-500 focus:outline-none',
                        required: true,
                        step: '0.01',
                        min: '0',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 1303 },
                      })
                    )
                  ),
                  React.createElement(
                    'div',
                    { __self: this, __source: { fileName: _jsxFileName, lineNumber: 1320 } },
                    React.createElement(
                      'label',
                      {
                        className:
                          'block text-xs uppercase tracking-widest text-stone-500 font-bold mb-2',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 1321 },
                      },
                      'Stock'
                    ),
                    React.createElement('input', {
                      type: 'number',
                      value: currentProduct.stock || '',
                      onChange: (e) =>
                        setCurrentProduct({
                          ...currentProduct,
                          stock: parseInt(e.target.value) || 0,
                        }),
                      className:
                        'w-full border border-stone-300 p-3 text-sm rounded-sm focus:border-gold-500 focus:outline-none',
                      min: '0',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 1324 },
                    })
                  )
                ),

                /* Pre-order Options */
                React.createElement(
                  'div',
                  {
                    className: 'border-t border-stone-100 pt-4',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 1335 },
                  },
                  React.createElement(
                    'div',
                    {
                      className: 'flex items-center gap-2 mb-3',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 1336 },
                    },
                    React.createElement('input', {
                      type: 'checkbox',
                      id: 'isPreOrder',
                      checked: isPreOrder,
                      onChange: (e) => setIsPreOrder(e.target.checked),
                      className: 'w-4 h-4 text-gold-600 rounded',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 1337 },
                    }),
                    React.createElement(
                      'label',
                      {
                        htmlFor: 'isPreOrder',
                        className: 'text-xs uppercase tracking-widest text-stone-500 font-bold',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 1344 },
                      },
                      'Pre-Order Item'
                    )
                  ),

                  isPreOrder &&
                    React.createElement(
                      'div',
                      { __self: this, __source: { fileName: _jsxFileName, lineNumber: 1350 } },
                      React.createElement(
                        'label',
                        {
                          className:
                            'block text-xs uppercase tracking-widest text-stone-500 font-bold mb-2',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1351 },
                        },
                        'Expected Release Date'
                      ),
                      React.createElement('input', {
                        type: 'date',
                        value: preOrderDate,
                        onChange: (e) => setPreOrderDate(e.target.value),
                        className:
                          'w-full border border-stone-300 p-3 text-sm rounded-sm focus:border-gold-500 focus:outline-none',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 1354 },
                      })
                    )
                ),

                React.createElement(
                  'div',
                  {
                    className: 'flex gap-4 pt-4 border-t border-stone-100',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 1364 },
                  },
                  React.createElement(
                    'label',
                    {
                      className: 'flex items-center gap-2',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 1365 },
                    },
                    React.createElement('input', {
                      type: 'checkbox',
                      checked: currentProduct.isNew || false,
                      onChange: (e) =>
                        setCurrentProduct({ ...currentProduct, isNew: e.target.checked }),
                      className: 'w-4 h-4 text-gold-600 rounded',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 1366 },
                    }),
                    React.createElement(
                      'span',
                      {
                        className: 'text-sm text-stone-700',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 1372 },
                      },
                      'New Arrival'
                    )
                  ),
                  React.createElement(
                    'label',
                    {
                      className: 'flex items-center gap-2',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 1374 },
                    },
                    React.createElement('input', {
                      type: 'checkbox',
                      checked: currentProduct.isBestSeller || false,
                      onChange: (e) =>
                        setCurrentProduct({ ...currentProduct, isBestSeller: e.target.checked }),
                      className: 'w-4 h-4 text-gold-600 rounded',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 1375 },
                    }),
                    React.createElement(
                      'span',
                      {
                        className: 'text-sm text-stone-700',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 1381 },
                      },
                      'Best Seller'
                    )
                  )
                )
              ),

              /* Right Column */
              React.createElement(
                'div',
                {
                  className: 'space-y-6',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 1387 },
                },
                React.createElement(
                  'div',
                  { __self: this, __source: { fileName: _jsxFileName, lineNumber: 1388 } },
                  React.createElement(
                    'label',
                    {
                      className:
                        'block text-xs uppercase tracking-widest text-stone-500 font-bold mb-2',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 1389 },
                    },
                    'Condition'
                  ),
                  React.createElement(
                    'select',
                    {
                      value: currentProduct.condition || 'Brand New',
                      onChange: (e) =>
                        setCurrentProduct({ ...currentProduct, condition: e.target.value }),
                      className:
                        'w-full border border-stone-300 p-3 text-sm rounded-sm focus:border-gold-500 focus:outline-none',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 1392 },
                    },

                    conditionOptions.map((option) =>
                      React.createElement(
                        'option',
                        {
                          key: option.value,
                          value: option.value,
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1398 },
                        },
                        option.label
                      )
                    )
                  )
                ),

                /* Jewelry Specific Fields */
                currentProduct.category === 'jewelry' &&
                  React.createElement(
                    React.Fragment,
                    null,
                    React.createElement(
                      'div',
                      { className: 'bg-stone-50 p-4 border border-stone-200 rounded-sm' },
                      React.createElement(
                        'h3',
                        {
                          className:
                            'text-xs uppercase tracking-widest text-stone-500 font-bold mb-4 flex items-center gap-2',
                        },
                        React.createElement(Gem, { size: 14 }),
                        ' Jewelry Specifications'
                      ),

                      /* Metal Type Selector */
                      React.createElement(
                        'div',
                        { className: 'mb-4' },
                        React.createElement(
                          'label',
                          {
                            className:
                              'block text-xs uppercase tracking-widest text-stone-500 font-bold mb-2',
                          },
                          'Metal'
                        ),
                        React.createElement(
                          'div',
                          { className: 'flex gap-2' },
                          React.createElement(
                            'button',
                            {
                              type: 'button',
                              onClick: () =>
                                setCurrentProduct({ ...currentProduct, metal_type: 'gold' }),
                              className: `flex-1 py-2 text-xs font-bold border rounded-sm transition-colors ${(currentProduct.metal_type || 'gold') === 'gold' ? 'bg-amber-500 text-white border-amber-500' : 'bg-white text-stone-600 border-stone-300 hover:border-amber-400'}`,
                            },
                            '✨ Gold'
                          ),
                          React.createElement(
                            'button',
                            {
                              type: 'button',
                              onClick: () =>
                                setCurrentProduct({ ...currentProduct, metal_type: 'silver' }),
                              className: `flex-1 py-2 text-xs font-bold border rounded-sm transition-colors ${currentProduct.metal_type === 'silver' ? 'bg-slate-500 text-white border-slate-500' : 'bg-white text-stone-600 border-stone-300 hover:border-slate-400'}`,
                            },
                            '◇ Silver'
                          )
                        )
                      ),

                      React.createElement(
                        'div',
                        { className: 'grid grid-cols-2 gap-4' },

                        /* Gold fields */
                        (currentProduct.metal_type || 'gold') === 'gold' &&
                          React.createElement(
                            React.Fragment,
                            null,
                            React.createElement(
                              'div',
                              null,
                              React.createElement(
                                'label',
                                {
                                  className:
                                    'block text-xs uppercase tracking-widest text-stone-500 font-bold mb-2',
                                },
                                'Karat'
                              ),
                              React.createElement(
                                'select',
                                {
                                  value: currentProduct.karat || '18k',
                                  onChange: (e) =>
                                    setCurrentProduct({ ...currentProduct, karat: e.target.value }),
                                  className:
                                    'w-full border border-stone-300 p-3 text-sm rounded-sm focus:border-gold-500 focus:outline-none',
                                },
                                React.createElement('option', { value: '24k' }, '24k'),
                                React.createElement('option', { value: '22k' }, '22k'),
                                React.createElement('option', { value: '21k' }, '21k'),
                                React.createElement('option', { value: '18k' }, '18k'),
                                React.createElement('option', { value: '14k' }, '14k'),
                                React.createElement('option', { value: '10k' }, '10k')
                              )
                            ),
                            React.createElement(
                              'div',
                              null,
                              React.createElement(
                                'label',
                                {
                                  className:
                                    'block text-xs uppercase tracking-widest text-stone-500 font-bold mb-2',
                                },
                                'Weight (g)'
                              ),
                              React.createElement(
                                'div',
                                { className: 'relative' },
                                React.createElement('input', {
                                  type: 'text',
                                  value: currentProduct.weight || '',
                                  onChange: (e) =>
                                    setCurrentProduct({
                                      ...currentProduct,
                                      weight: e.target.value,
                                    }),
                                  className:
                                    'w-full pr-8 pl-3 py-3 border border-stone-300 text-sm rounded-sm focus:border-gold-500 focus:outline-none',
                                  placeholder: 'e.g., 5.00',
                                }),
                                React.createElement(
                                  'span',
                                  {
                                    className:
                                      'absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 text-xs',
                                  },
                                  'g'
                                )
                              )
                            ),
                            React.createElement(
                              'div',
                              { className: 'col-span-2' },
                              React.createElement(
                                'label',
                                {
                                  className:
                                    'block text-xs uppercase tracking-widest text-stone-500 font-bold mb-2',
                                },
                                'Gold Type'
                              ),
                              React.createElement(
                                'select',
                                {
                                  value: currentProduct.goldType || 'Saudi Gold',
                                  onChange: (e) =>
                                    setCurrentProduct({
                                      ...currentProduct,
                                      goldType: e.target.value,
                                    }),
                                  className:
                                    'w-full border border-stone-300 p-3 text-sm rounded-sm focus:border-gold-500 focus:outline-none',
                                },
                                React.createElement(
                                  'option',
                                  { value: 'Saudi Gold' },
                                  'Saudi Gold'
                                ),
                                React.createElement(
                                  'option',
                                  { value: 'Italian Gold' },
                                  'Italian Gold'
                                ),
                                React.createElement(
                                  'option',
                                  { value: 'Turkish Gold' },
                                  'Turkish Gold'
                                ),
                                React.createElement(
                                  'option',
                                  { value: 'Emirati Gold' },
                                  'Emirati Gold'
                                ),
                                React.createElement(
                                  'option',
                                  { value: 'Local PH Gold' },
                                  'Local PH Gold'
                                ),
                                React.createElement(
                                  'option',
                                  { value: 'Ongpin Gold' },
                                  'Ongpin Gold'
                                ),
                                React.createElement(
                                  'option',
                                  { value: 'Japan Gold' },
                                  'Japan Gold'
                                ),
                                React.createElement('option', { value: 'China Gold' }, 'China Gold')
                              )
                            )
                          ),

                        /* Silver fields */
                        currentProduct.metal_type === 'silver' &&
                          React.createElement(
                            React.Fragment,
                            null,
                            React.createElement(
                              'div',
                              null,
                              React.createElement(
                                'label',
                                {
                                  className:
                                    'block text-xs uppercase tracking-widest text-stone-500 font-bold mb-2',
                                },
                                'Purity'
                              ),
                              React.createElement(
                                'select',
                                {
                                  value: currentProduct.silver_purity || '925',
                                  onChange: (e) => {
                                    const purityLabels = {
                                      999: 'Fine Silver',
                                      925: 'Sterling Silver',
                                      900: 'Coin Silver',
                                      800: 'European Silver',
                                    };
                                    setCurrentProduct({
                                      ...currentProduct,
                                      silver_purity: e.target.value,
                                      silver_type:
                                        purityLabels[e.target.value] || 'Sterling Silver',
                                    });
                                  },
                                  className:
                                    'w-full border border-stone-300 p-3 text-sm rounded-sm focus:border-gold-500 focus:outline-none',
                                },
                                React.createElement(
                                  'option',
                                  { value: '999' },
                                  '999 (Fine Silver)'
                                ),
                                React.createElement(
                                  'option',
                                  { value: '925' },
                                  '925 (Sterling Silver)'
                                ),
                                React.createElement(
                                  'option',
                                  { value: '900' },
                                  '900 (Coin Silver)'
                                ),
                                React.createElement(
                                  'option',
                                  { value: '800' },
                                  '800 (European Silver)'
                                )
                              )
                            ),
                            React.createElement(
                              'div',
                              null,
                              React.createElement(
                                'label',
                                {
                                  className:
                                    'block text-xs uppercase tracking-widest text-stone-500 font-bold mb-2',
                                },
                                'Weight (g)'
                              ),
                              React.createElement(
                                'div',
                                { className: 'relative' },
                                React.createElement('input', {
                                  type: 'text',
                                  value: currentProduct.weight || '',
                                  onChange: (e) =>
                                    setCurrentProduct({
                                      ...currentProduct,
                                      weight: e.target.value,
                                    }),
                                  className:
                                    'w-full pr-8 pl-3 py-3 border border-stone-300 text-sm rounded-sm focus:border-gold-500 focus:outline-none',
                                  placeholder: 'e.g., 5.00',
                                }),
                                React.createElement(
                                  'span',
                                  {
                                    className:
                                      'absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 text-xs',
                                  },
                                  'g'
                                )
                              )
                            )
                          )
                      )
                    ),

                    React.createElement(
                      'div',
                      {
                        className: 'flex items-center gap-2',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 1465 },
                      },
                      React.createElement('input', {
                        type: 'checkbox',
                        id: 'manualPrice',
                        checked: useManualPrice,
                        onChange: (e) => {
                          setUseManualPrice(e.target.checked);
                          if (e.target.checked) {
                            setManualPrice(currentProduct.price || 0);
                          } else {
                            setCurrentProduct((prev) => ({ ...prev, priceOverridden: false }));
                          }
                        },
                        className: 'w-4 h-4 text-gold-600 rounded',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 1466 },
                      }),
                      React.createElement(
                        'label',
                        {
                          htmlFor: 'manualPrice',
                          className: 'text-xs uppercase tracking-widest text-stone-500 font-bold',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1480 },
                        },
                        'Override Auto-Calculated Price'
                      )
                    ),

                    React.createElement(
                      'div',
                      {
                        className:
                          'bg-blue-50 border border-blue-100 p-3 rounded-sm text-xs text-blue-700',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 1485 },
                      },
                      React.createElement(
                        'p',
                        {
                          className: 'flex items-center gap-1',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1486 },
                        },
                        React.createElement(Calculator, {
                          size: 12,
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1487 },
                        }),
                        'Price auto-calculated based on weight and gold rate.'
                      )
                    )
                  ),

                /* For non-jewelry categories */
                currentProduct.category !== 'jewelry' &&
                  React.createElement(
                    'div',
                    {
                      className: 'bg-stone-50 p-4 border border-stone-200 rounded-sm',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 1496 },
                    },
                    React.createElement(
                      'h3',
                      {
                        className:
                          'text-xs uppercase tracking-widest text-stone-500 font-bold mb-2',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 1497 },
                      },
                      'Product Details'
                    ),
                    React.createElement(
                      'p',
                      {
                        className: 'text-xs text-stone-400',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 1500 },
                      },
                      'No additional specifications needed for ',
                      currentProduct.category === 'bags'
                        ? 'bags'
                        : currentProduct.category === 'fragrances'
                          ? 'fragrances'
                          : 'this category',
                      '.'
                    )
                  ),

                /* Image Upload Section */
                React.createElement(
                  'div',
                  {
                    className: 'border-t border-stone-100 pt-4',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 1507 },
                  },
                  React.createElement(
                    'label',
                    {
                      className:
                        'block text-xs uppercase tracking-widest text-stone-500 font-bold mb-2',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 1508 },
                    },
                    'Main Image'
                  ),
                  React.createElement(
                    'div',
                    {
                      className: 'border-2 border-dashed border-stone-300 rounded-sm p-4',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 1511 },
                    },
                    currentProduct.image
                      ? React.createElement(
                          'div',
                          {
                            className: 'relative group',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 1513 },
                          },
                          React.createElement('img', {
                            src: normalizeImageUrl(currentProduct.image),
                            alt: 'Preview',
                            className: 'w-full h-48 object-cover rounded-sm',
                            onError: (e) => {
                              e.target.src = PLACEHOLDER_IMAGE;
                            },
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 1514 },
                          }),
                          React.createElement(
                            'button',
                            {
                              type: 'button',
                              onClick: () => setCurrentProduct({ ...currentProduct, image: '' }),
                              className:
                                'absolute top-2 right-2 bg-white rounded-full p-1 shadow-sm hover:text-red-500',
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 1522 },
                            },

                            React.createElement(X, {
                              size: 16,
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 1527 },
                            })
                          )
                        )
                      : React.createElement(
                          'label',
                          {
                            className:
                              'cursor-pointer flex flex-col items-center justify-center h-48',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 1531 },
                          },
                          React.createElement(Upload, {
                            size: 24,
                            className: 'text-stone-400 mb-2',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 1532 },
                          }),
                          React.createElement(
                            'span',
                            {
                              className: 'text-xs text-stone-500',
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 1533 },
                            },
                            'Click to upload main image'
                          ),
                          React.createElement('input', {
                            type: 'file',
                            className: 'hidden',
                            accept: 'image/*',
                            onChange: handleMainImageChange,
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 1534 },
                          })
                        )
                  )
                ),

                /* Gallery Images */
                React.createElement(
                  'div',
                  {
                    className: 'border-t border-stone-100 pt-4',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 1546 },
                  },
                  React.createElement(
                    'label',
                    {
                      className:
                        'block text-xs uppercase tracking-widest text-stone-500 font-bold mb-2',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 1547 },
                    },
                    'Gallery Images'
                  ),
                  React.createElement(
                    'div',
                    {
                      className: 'border-2 border-dashed border-stone-300 rounded-sm p-4',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 1550 },
                    },
                    galleryImages.length > 0
                      ? React.createElement(
                          'div',
                          {
                            className: 'grid grid-cols-3 gap-2',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 1552 },
                          },
                          galleryImages.map((img, index) =>
                            React.createElement(
                              'div',
                              {
                                key: index,
                                className: 'relative group',
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 1554 },
                              },
                              React.createElement('img', {
                                src: normalizeImageUrl(img),
                                alt: `Gallery ${index + 1}`,
                                className: 'w-full h-20 object-cover rounded-sm',
                                onError: (e) => {
                                  e.target.src = PLACEHOLDER_IMAGE;
                                },
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 1555 },
                              }),
                              React.createElement(
                                'button',
                                {
                                  type: 'button',
                                  onClick: () => handleRemoveGalleryImage(index),
                                  className:
                                    'absolute top-1 right-1 bg-white rounded-full p-0.5 shadow-sm hover:text-red-500',
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 1563 },
                                },

                                React.createElement(X, {
                                  size: 12,
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 1568 },
                                })
                              )
                            )
                          ),
                          React.createElement(
                            'label',
                            {
                              className:
                                'cursor-pointer flex items-center justify-center h-20 border border-dashed border-stone-300 rounded-sm hover:border-gold-500',
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 1572 },
                            },
                            React.createElement(Plus, {
                              size: 16,
                              className: 'text-stone-400',
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 1573 },
                            }),
                            React.createElement('input', {
                              type: 'file',
                              className: 'hidden',
                              accept: 'image/*',
                              multiple: true,
                              onChange: handleGalleryUpload,
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 1574 },
                            })
                          )
                        )
                      : React.createElement(
                          'label',
                          {
                            className:
                              'cursor-pointer flex flex-col items-center justify-center h-20',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 1584 },
                          },
                          React.createElement(Plus, {
                            size: 20,
                            className: 'text-stone-400 mb-1',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 1585 },
                          }),
                          React.createElement(
                            'span',
                            {
                              className: 'text-[10px] text-stone-500',
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 1586 },
                            },
                            'Add gallery images'
                          ),
                          React.createElement('input', {
                            type: 'file',
                            className: 'hidden',
                            accept: 'image/*',
                            multiple: true,
                            onChange: handleGalleryUpload,
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 1587 },
                          })
                        )
                  ),
                  React.createElement(
                    'p',
                    {
                      className: 'text-[10px] text-stone-400 mt-2',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 1597 },
                    },
                    'Recommended: Upload square images (1:1 ratio) for best display.'
                  )
                )
              ),

              /* Form Actions */
              React.createElement(
                'div',
                {
                  className: 'md:col-span-2 flex justify-end gap-4 pt-6 border-t border-stone-100',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 1604 },
                },
                React.createElement(
                  'button',
                  {
                    type: 'button',
                    onClick: handleCancel,
                    className:
                      'px-6 py-3 border border-stone-300 text-stone-600 text-xs uppercase tracking-widest hover:border-stone-900 hover:text-stone-900 transition-colors',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 1605 },
                  },
                  'Cancel'
                ),
                React.createElement(
                  'button',
                  {
                    type: 'submit',
                    disabled: isSaving,
                    className:
                      'px-8 py-3 bg-stone-900 text-white text-xs uppercase tracking-widest hover:bg-gold-600 transition-colors shadow-md disabled:opacity-50 flex items-center gap-2',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 1612 },
                  },

                  isSaving
                    ? React.createElement(
                        React.Fragment,
                        null,
                        React.createElement(RefreshCw, {
                          size: 14,
                          className: 'animate-spin',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1619 },
                        }),
                        'Saving...'
                      )
                    : React.createElement(
                        React.Fragment,
                        null,
                        React.createElement(Save, {
                          size: 14,
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 1624 },
                        }),
                        currentProduct.id ? 'Update Product' : 'Save Product'
                      )
                )
              )
            )
          ),

      /* Promo Modal */
      React.createElement(AdminPromoModal, {
        isOpen: showPromoModal,
        onClose: () => setShowPromoModal(false),
        selectedProducts: products.filter((p) => selectedProducts.includes(p.id)),
        onApplyPromo: applyPromoToProducts,
        __self: this,
        __source: { fileName: _jsxFileName, lineNumber: 1635 },
      })
    )
  );
};

export default AdminProducts;
